import { BsPrinter } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
import { HiXMark } from "react-icons/hi2";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast } from "react-toastify";
import { PrintLayout } from "../PrintLayout";

const ViewBirthRecordModal = ({ isOpen, onClose, record, onEdit, onDelete }) => {
  if (!isOpen || !record) return null;

  const handlePrintBill = () => {
    // Hide all elements except the print layout
    document.body.style.visibility = 'hidden';
    const printLayout = document.querySelector('.print-birth-record');
    if (printLayout) {
      printLayout.style.visibility = 'hidden';
      printLayout.style.display = 'block';
    }

    window.print();

    // Restore visibility after printing
    setTimeout(() => {
      document.body.style.visibility = '';
      if (printLayout) {
        printLayout.style.display = 'none';
      }
    }, 1000);
  };

  const handleEdit = () => {
    onEdit(record);
    onClose();
  };

  const handleDelete = () => {
    onDelete(record.id);
    onClose();
  };

  const fields = [
    { label: "Child ID", value: record.id || "N/A" },
    { label: "Child Name", value: record.childName || "N/A" },
    { label: "Gender", value: record.gender || "N/A" },
    { label: "Weight", value: record.weight ? `${record.weight} Kg` : "N/A" },
    {
      label: "Birth Date",
      value: record.birthDate ? new Date(record.birthDate).toLocaleDateString() : "N/A",
    },
    { label: "Mother Name", value: record.motherName || "N/A" },
    { label: "Father Name", value: record.fatherName || "N/A" },
    { label: "Phone", value: record.phone || "N/A" },
    { label: "Address", value: record.address || "N/A" },
    { label: "Report", value: record.report || "N/A" },
  ];

  const images = [
    { key: "childPhoto", label: "Child Photo" },
    { key: "motherPhoto", label: "Mother Photo" },
    { key: "fatherPhoto", label: "Father Photo" },
    { key: "documentPhoto", label: "Document Photo" },
  ];

  // Define button configurations with custom styles
  const buttons = [
    {
      icon: <BsPrinter size={16} />,
      onClick: handlePrintBill,
      title: "Print Bill",
      className:
        "size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300",
    },
    {
      icon: <FiEdit size={16} />,
      onClick: handleEdit,
      title: "Edit",
      className:
        "size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300",
    },
    {
      icon: <RiDeleteBin6Line size={16} />,
      onClick: handleDelete,
      title: "Delete",
      className:
        "size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300",
    },
    {
      icon: <HiXMark size={16} />,
      onClick: () => {
        toast.dismiss();
        onClose();
      },
      title: "Close",
      className:
        "size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300",
    },
  ];

  // Prepare data for PrintLayout
  const billInfo = (
    <>
      <div>
        <p><strong>Child ID:</strong> {record.id || "N/A"}</p>
        <p><strong>Child Name:</strong> {record.childName || "N/A"}</p>
        <p><strong>Gender:</strong> {record.gender || "N/A"}</p>
        <p><strong>Weight:</strong> {record.weight ? `${record.weight} Kg` : "N/A"}</p>
        <p><strong>Birth Date:</strong> {record.birthDate ? new Date(record.birthDate).toLocaleDateString() : "N/A"}</p>
      </div>
      <div>
        <p><strong>Mother Name:</strong> {record.motherName || "N/A"}</p>
        <p><strong>Father Name:</strong> {record.fatherName || "N/A"}</p>
        <p><strong>Phone:</strong> {record.phone || "N/A"}</p>
        <p><strong>Address:</strong> {record.address || "N/A"}</p>
      </div>
    </>
  );
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 print-hidden">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95%] sm:max-w-[90%] md:max-w-[1000px] max-h-[90vh] overflow-y-auto print-content">
          {/* Modal Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap border-b border-dashed border-gray-300 p-4 print-hidden">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">View Birth Record</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {buttons.map((btn, index) => (
                <button
                  key={index}
                  onClick={btn.onClick}
                  className={`${btn.className}`}
                  title={btn.title}
                >
                  {btn.icon}
                </button>
              ))}
            </div>
          </div>
          {/* Modal Body */}
          <div className="p-4 sm:p-6">
            {/* Table for Record Details */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200 print-hidden">
                  {fields.map((field, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-blue-50 transition-colors duration-200 print-hidden ${
                        index % 2 === 0 ? "bg-gray-50/50" : "bg-white"
                      }`}
                    >
                      <th className="px-4 py-3 text-left font-bold text-gray-700 text-xs sm:text-sm w-[40%] sm:w-[30%]">
                        {field.label}
                      </th>
                      <td className="px-4 py-3 text-gray-900 font-inter text-xs sm:text-sm break-words">
                        {field.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Images Section */}
           <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4 print-hidden">
              {images.map(({ key, label }) => (
                <div
                  key={key}
                  className="flex flex-col"
                >
                  <label className="text-sm font-semibold text-gray-800 mb-2 tracking-wide">
                    {label}
                  </label>
                  {record[key] ? (
                    <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden border border-gray-300 shadow-sm">
                      <img
                        src={`https://api.muktihospital.com/uploads/${record[key]}`}
                        alt={label}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                        onError={(e) => {
                          e.target.src = "/path/to/placeholder-image.jpg";
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center bg-gray-200 rounded-lg border border-gray-300">
                      <p className="text-xs text-gray-500 font-medium italic">No Image</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Print Layout */}
      <PrintLayout
        className="print-birth-record"
        title="Birth Record"
        billInfo={billInfo}
      />
    </>
  );
};

export default ViewBirthRecordModal;